<template>
  <el-select
    ref="selectRef"
    :model-value="modelValue"
    :suffix-icon="useIcon('chevron-down')"
    :clear-icon="useIcon('custom/clear')"
    :class="{
      'is-transparent': props.transparent,
    }"
    :multiple="props.multiple"
    :multiple-limit="props.multipleLimit"
    :filterable="props.filterable || props.addable"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :collapse-tags="props.collapseTags"
    :clearable="props.clearable"
    :default-first-option="props.addable"
    :reserve-keyword="false"
    :remote-method="props.remoteMethod"
    :remote="!!props.remoteMethod"
    :persistent="props.persistent"
    :filter-method="props.remoteMethod ? undefined : filterMethod"
    @update:model-value="onUpdate"
  >
    <template
      v-if="props.prefixIcon || currentOption?.icon || currentOption?.avatar || currentOption?.thumbnail"
      #prefix
    >
      <Icon
        v-if="props.prefixIcon || currentOption?.icon"
        :name="props.prefixIcon || currentOption.icon"
        class="w-4 h-4 text-gray-500"
      />
      <Avatar
        v-else-if="currentOption?.avatar"
        :image="currentOption.avatar.image"
        :name="currentOption.avatar.name"
        size="xs"
      />
      <Thumbnail
        v-else-if="currentOption?.thumbnail"
        :image="currentOption.thumbnail"
        :name="currentOption.text"
        size="xs"
      />
    </template>
    <el-option
      v-for="option in optionsFiltered"
      :key="option.id"
      :label="option.text"
      :value="option.id"
      :data-option="option.id"
      :disabled="option.isDisabled"
    >
      <div
        v-if="option?.icon || option?.avatar || option?.thumbnail"
        class="mr-3 h-6 flex items-center"
      >
        <Icon
          v-if="option?.icon"
          :name="option.icon"
          class="w-4 h-4 text-gray-500"
        />
        <Avatar
          v-else-if="option?.avatar"
          :image="option.avatar.image"
          :name="option.avatar.name"
          size="xs"
        />
        <Thumbnail
          v-else-if="option?.thumbnail"
          :image="option.thumbnail"
          :name="option.text"
          size="xs"
        />
      </div>
      <div class="w-full">
        <div class="mt-0.5 truncate">
          {{ option.text }}
        </div>
        <div
          v-if="option.description"
          class="text-sm text-gray-500 font-normal"
          v-html="sanitize(option.description)"
        />
      </div>
    </el-option>

    <el-option
      v-if="props.addable && query && !props.options.find((option) => option.text === query)"
      :key="query"
      :label="query"
      value="addableItem"
      class="flex items-center [.is-empty_&]:m-2"
    >
      <div class="text-sm text-gray-700 mr-3">
        {{ useT('ds.create') }}
      </div>
      <div class="el-tag el-tag--info">
        {{ query }}
      </div>
    </el-option>

    <template
      v-if="props.addable && query"
      #empty
    />
  </el-select>
</template>

<script setup>
const props = defineProps(buildProps({
  modelValue: {
    type: [String, Array, Number],
  },
  options: {
    type: Array,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  multipleLimit: {
    type: Number,
    default: 0,
  },
  transparent: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  collapseTags: {
    type: Boolean,
    default: false,
  },
  filterable: {
    type: Boolean,
    default: false,
  },
  addable: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  remoteMethod: {
    type: Function,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  persistent: {
    type: Boolean,
    default: true,
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  missingValueText: {
    type: String,
    default: '',
  },
}));

const emit = defineEmits(['update:modelValue', 'add']);

// Refs
const selectRef = ref(null);
const query = ref('');

// Computed
const currentOption = computed(() => props.options.find((option) => option.id === (props.modelValue ?? null)));
const modelValue = computed(() => {
  if (props.remoteMethod) {
    return props.modelValue;
  }

  if (props.multiple) {
    return Array.isArray(props.modelValue)
      ? props.modelValue.filter((item) => props.options.some((option) => option.id === item)) // filter out values that are not in the options
      : props.modelValue;
  } else if (!currentOption.value) {
    return props.missingValueText;
  }

  return props.modelValue;
});
const optionsFiltered = computed(() => (props.filterable || props.addable) && query.value && !props.remoteMethod ? props.options.filter((option) => normalizeString(option.text).includes(normalizeString(query.value))) : props.options);

// Methods
const onUpdate = (value) => {
  if (value === 'addableItem' || (props.multiple && value.includes('addableItem'))) {
    onAdd();
  } else {
    emit('update:modelValue', value);
  }
};

const onAdd = () => {
  emit('add', query.value);
};

const filterMethod = (payload) => {
  query.value = payload;
};

// Lifecycle
onMounted(() => {
  if (!props.persistent && !props.multiple) {
    watch(() => currentOption.value, (value) => {
      if (value) {
        selectRef.value.selectedLabel = value.text;
      }
    }, { immediate: true });
  }
});

defineExpose({
  selectRef,
});
</script>
